import React from 'react'
import { useFocusWithin, useHover, useInteractOutside } from 'react-aria'

import { styled, Theme } from '~/styled'
import { cn } from '~/styled'

import {
  SideBarBody,
  SideBarBrand,
  SideBarBrandLogo,
  SideBarBrandName,
  SideBarFooter,
  SideBarHeader
} from './SideBarComponents'
import { SideBarContext } from './SideBarContext'

const light = {
  '--background': 'white',
  '--text': 'var(--color-grey-800)',
  '--border': 'var(--color-grey-200)'
}

const Root = styled('div', {
  base: ['sticky', 'z-1'],
  variants: {
    type: {
      static: [],
      expandable: ['w-22']
    }
  }
})

const Content = styled('div', {
  base: [
    'bg-(--background)',
    'border-r',
    'border-r-(--border)',
    'shadow-[4px_0_4px_-2px_rgba(31,31,31,0)]',
    'flex',
    'flex-col',
    'h-full',
    'overflow-hidden',
    'will-change-width',
    'motion-safe:duration-125',
    'motion-safe:ease-out',
    'motion-safe:transition-[shadow,width]'
  ],
  variants: {
    type: {
      static: [],
      expandable: []
    },
    isExpanded: {
      true: ['w-[16rem]'],
      false: ['w-22']
    }
  },
  compoundVariants: [
    {
      isExpanded: true,
      type: 'expandable',
      class: ['shadow-[4px_0_4px_-2px_rgba(31,31,31,0.1)]']
    }
  ]
})

const PointerBlocker = styled('div', {
  base: [
    'bottom-0',
    'left-0',
    'absolute',
    'right-0',
    'top-0',
    'transition-all'
  ],
  variants: {
    isVisible: {
      true: ['delay-0', 'visible'],
      false: ['delay-50', 'invisible']
    }
  }
})

type SideBarProps = React.ComponentProps<typeof Root> & {
  className?: string
  offset?: number | string
} & React.ComponentProps<typeof Theme>

export const SideBar = ({
  theme,
  className,
  children,
  type = 'expandable',
  offset = '0px',
  style,
  ...rest
}: SideBarProps) => {
  const [isExpanded, setIsExpanded] = React.useState(type === 'static')
  const ref = React.useRef<HTMLDivElement>(null)

  useInteractOutside({
    ref,
    onInteractOutside: () => setIsExpanded(false)
  })
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setIsExpanded(true),
    onBlurWithin: () => setIsExpanded(false)
  })
  const { hoverProps, isHovered } = useHover({
    onHoverStart: () => setIsExpanded(true),
    onHoverEnd: () => setIsExpanded(false)
  })
  const touchProps = {
    onTouchEnd: () => setIsExpanded(true)
  }

  const expandableProps =
    type === 'expandable'
      ? {
          ...focusWithinProps,
          ...hoverProps,
          ...touchProps,
          ref
        }
      : {}

  return (
    <SideBarContext.Provider value={{ isExpanded }}>
      <Theme theme={theme || light}>
        <Root
          {...rest}
          style={{ ...style, '--offset': offset }}
          type={type}
          className={cn(
            'h-[calc(100svh-var(--offset))]',
            'top-(--offset)',
            className
          )}
        >
          <Content {...expandableProps} isExpanded={isExpanded} type={type}>
            {children}
            {/**
             * When the SideBar is collapsed, ensure that the initial tap event
             * is used to expand the nav first before making the items available
             */}
            {type === 'expandable' && (
              <PointerBlocker isVisible={!isHovered && !isExpanded} />
            )}
          </Content>
        </Root>
      </Theme>
    </SideBarContext.Provider>
  )
}

SideBar.Brand = SideBarBrand
SideBar.BrandLogo = SideBarBrandLogo
SideBar.BrandName = SideBarBrandName
SideBar.Header = SideBarHeader
SideBar.Body = SideBarBody
SideBar.Footer = SideBarFooter

SideBar.displayName = 'SideBar'
